import * as XLSX from 'xlsx';

export type XlsxCell = string | number | boolean | Date | null | undefined;

export type XlsxSheetSpec = {
  name: string;
  headers: string[];
  rows: XlsxCell[][];
  /** 0-based column indexes to format as currency (two decimal dollars). */
  currencyCols?: number[];
};

function sheetName(raw: string): string {
  const cleaned = raw.replace(/[\\/?*[\]:]/g, ' ').trim() || 'Sheet';
  return cleaned.slice(0, 31);
}

function applyCurrencyFormats(ws: XLSX.WorkSheet, spec: XlsxSheetSpec) {
  const cols = spec.currencyCols ?? [];
  if (cols.length === 0) return;
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let r = range.s.r + 1; r <= range.e.r; r += 1) {
    for (const c of cols) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr] as XLSX.CellObject | undefined;
      if (cell && typeof cell.v === 'number') {
        cell.t = 'n';
        cell.z = '$#,##0.00';
      }
    }
  }
}

function autoWidth(headers: string[], rows: XlsxCell[][]): XLSX.ColInfo[] {
  return headers.map((h, i) => {
    let max = h.length;
    for (const row of rows) {
      const v = row[i];
      const len = v == null ? 0 : String(v).length;
      if (len > max) max = len;
    }
    return { wch: Math.min(40, Math.max(10, max + 2)) };
  });
}

export function downloadXlsx(filename: string, sheets: XlsxSheetSpec[]): void {
  const wb = XLSX.utils.book_new();
  const used = new Set<string>();
  for (const spec of sheets) {
    let name = sheetName(spec.name);
    let n = 2;
    while (used.has(name.toLowerCase())) {
      const suffix = ` ${n}`;
      name = `${sheetName(spec.name).slice(0, 31 - suffix.length)}${suffix}`;
      n += 1;
    }
    used.add(name.toLowerCase());
    const aoa: XlsxCell[][] = [spec.headers, ...spec.rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    applyCurrencyFormats(ws, spec);
    ws['!cols'] = autoWidth(spec.headers, spec.rows);
    XLSX.utils.book_append_sheet(wb, ws, name);
  }
  const file = filename.toLowerCase().endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, file);
}

export function dollarsFromCents(cents: number | null | undefined): number | null {
  if (cents == null || Number.isNaN(cents)) return null;
  return Math.round(cents) / 100;
}

export function yesNo(v: boolean | null | undefined): string {
  if (v == null) return '';
  return v ? 'yes' : 'no';
}

export async function fetchAllReportRows<T>(opts: {
  pageSize: number;
  maxRows: number;
  fetchPage: (skip: number, take: number) => Promise<{ rows: T[]; total: number }>;
}): Promise<{ rows: T[]; total: number; capped: boolean }> {
  const rows: T[] = [];
  let total = 0;
  let skip = 0;
  while (rows.length < opts.maxRows) {
    const take = Math.min(opts.pageSize, opts.maxRows - rows.length);
    const page = await opts.fetchPage(skip, take);
    total = page.total;
    if (page.rows.length === 0) break;
    rows.push(...page.rows);
    skip += page.rows.length;
    if (rows.length >= total || page.rows.length < take) break;
  }
  return { rows, total, capped: total > rows.length };
}
